import { NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { AnyMaskedOptions, MaskedRange } from 'imask';
import moment, { Moment } from 'moment';
import { MuiDatepickerV2Format } from './supported-formats';


export function ngbDateToMoment(date: NgbDateStruct): Moment {
  return moment({ date: date.day, month: date.month - 1, year: date.year });
}

export function momentToNgbDate(date: Moment): NgbDate {
  return NgbDate.from({ day: date.date(), month: date.month() + 1, year: date.year() }) as NgbDate;
}

export function iso8601StringToNgbDate(date: string): NgbDate {
  const m = moment(date);
  return NgbDate.from({ day: m.date(), month: m.month() + 1, year: m.year() }) as NgbDate;
}

function getBlockMaskOption(
  from: number,
  to: number,
  placeholderChar?: string,
  autofix?: boolean | 'pad'
): AnyMaskedOptions {
  return { mask: MaskedRange, placeholderChar, from, to, autofix };
}

interface CreateMaskOptions {
  pattern: MuiDatepickerV2Format;
  placeholderChars: { year: string, month: string, day: string };
  min?: string;
  max?: string;
}

interface CreateRangeMaskOptions extends CreateMaskOptions {
  separator: string;
}

export function createMaskOptions({ pattern, min, max, placeholderChars }: CreateMaskOptions): AnyMaskedOptions {
  const minYear = min ? moment(min).year() : 1900;
  const maxYear = max ? moment(max).year() : 2100;
  const { year: Y, month: M, day: D } = placeholderChars;

  // Add "shift back" characters after the separators in the pattern, see https://imask.js.org/guide.html#masked-pattern
  const finalPattern = pattern.replace(/([-./])/g, '$1`');

  return {
    mask: Date,
    pattern: finalPattern,
    lazy: false,
    eager: true,
    min: min ? moment(min).toDate() : undefined,
    max: max ? moment(max).toDate() : undefined,

    format: date => moment(date).format(pattern),
    parse: str => moment(str, pattern, true).toDate(),

    blocks: {
      /* eslint-disable @typescript-eslint/naming-convention */
      YYYY: getBlockMaskOption(minYear, maxYear, Y),
      YY: getBlockMaskOption(minYear, maxYear, Y),
      MM: getBlockMaskOption(1, 12, M, 'pad'),
      DD: getBlockMaskOption(1, 31, D, 'pad')

      /* ToDo - Add support for short formats (e.g. D.M.YY):
       *  IMask adds 2 placeholder characters anyway, so we cannot support this format properly yet. To support it, we need to
       *  use a custom MaskedPattern mask here. Without further effort, we would lose the autofix and min/max features though,
       *  so we skip it for now.  */
      //M: getBlockMaskOption(1, 12, 'M'),
      //D: getBlockMaskOption(1, 31, 'D')
    }
  };
}

export function createRangeMaskOption(opts: CreateRangeMaskOptions): AnyMaskedOptions {
  return {
    // Special characters are important, see https://imask.js.org/guide.html#masked-pattern
    mask: 'from{' + opts.separator + '}`to',
    lazy: false,
    eager: false,
    blocks: {
      from: createMaskOptions(opts),
      to: createMaskOptions(opts)
    }
  };
}
